import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import NoData from '@root/components/common/NoData';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@root/components/ui/Chart';
import ContentBox from '@root/components/v2/content/ContentBox';
import { useTheme } from '@root/context/ThemeContext';
import { CATEGORICAL_COLORS, SEVERITY_COLORS } from '@root/utilities/pastelColors';
import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import CardTitle from './CardTitle';
import {
  bucketTooltipLabel,
  CARD_BODY_HEIGHT,
  CARD_TITLE_STYLE,
  compactNumber,
  hasSeverityValues,
  toSeverityRows,
} from './cardUtils';

// "Unique CVEs by severity" trend card: distinct CVE IDs per day as overlaid Critical / High / Other
// lines, each over a soft gradient fill, with the legend above the plot. Other folds medium, low and
// unknown together. Renders the `get_vulnerabilities_unique_cves_trend` buckets, which share the
// `get_vulnerabilities_trend` shape.

const LEGEND_HEIGHT = 24;
const CHART_MARGIN_TOP = 12;
const Y_AXIS_WIDTH = 40;

// Drawn back to front, so the lowest series sits on top and its line stays visible.
const SERIES = [
  { key: 'critical', label: 'Critical', color: SEVERITY_COLORS.critical },
  { key: 'high', label: 'High', color: SEVERITY_COLORS.high },
  // Blue rather than the palette's severity `other` (rose pink), which reads too close to Critical.
  { key: 'other', label: 'Other', color: CATEGORICAL_COLORS.lightBlue },
];

const CHART_CONFIG = Object.fromEntries(SERIES.map(({ key, label, color }) => [key, { label, color }]));

const BODY_STYLE = { height: CARD_BODY_HEIGHT, padding: '6px 15px 10px', display: 'flex', flexDirection: 'column' };

// Mirrors the loaded body (legend row over the plot) so nothing jumps when data lands.
const TrendSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <Box sx={{ ...BODY_STYLE, gap: '16px' }}>
      <Box display="flex" gap="16px" height={LEGEND_HEIGHT} alignItems="center">
        {SERIES.map(series => (
          <Skeleton key={series.key} variant="text" width={56} height={16} sx={{ bgcolor: skeletonColor }} />
        ))}
      </Box>
      <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: '4px', bgcolor: skeletonColor }} />
    </Box>
  );
};

const UniqueCvesTrend = ({ data, isLoading = false, timeFilter, height = '300px' }) => {
  const rows = useMemo(() => toSeverityRows(data, timeFilter === '24h'), [data, timeFilter]);
  const hasCves = useMemo(() => hasSeverityValues(rows), [rows]);

  let body;
  if (isLoading) {
    body = <TrendSkeleton />;
  } else if (!hasCves) {
    body = (
      <Box sx={BODY_STYLE} justifyContent="center">
        <NoData message="No unique CVEs found" imageheight="60px" imageWidth="60px" />
      </Box>
    );
  } else {
    body = (
      <Box sx={BODY_STYLE}>
        <ChartContainer config={CHART_CONFIG} className="aspect-auto h-full w-full">
          <AreaChart data={rows} margin={{ top: CHART_MARGIN_TOP, right: 4, left: 0, bottom: 0 }}>
            <defs>
              {SERIES.map(series => (
                <linearGradient key={series.key} id={`uniqueCves-${series.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series.color} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              minTickGap={16}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickFormatter={value => compactNumber.format(value)}
            />
            <ChartTooltip content={<ChartTooltipContent indicator="line" labelFormatter={bucketTooltipLabel} />} />
            <ChartLegend
              verticalAlign="top"
              align="left"
              height={LEGEND_HEIGHT}
              wrapperStyle={{ top: 0, left: 0 }}
              content={<ChartLegendContent className="justify-start py-0 text-[12px]" />}
            />
            {SERIES.map(series => (
              <Area
                key={series.key}
                dataKey={series.key}
                type="linear"
                stroke={series.color}
                strokeWidth={1.5}
                fill={`url(#uniqueCves-${series.key})`}
                dot={false}
                activeDot={{ r: 3 }}
              />
            ))}
          </AreaChart>
        </ChartContainer>
      </Box>
    );
  }

  return (
    <ContentBox
      title={<CardTitle title="Unique CVEs" subtitle="By severity" />}
      titleContainerStyle={CARD_TITLE_STYLE}
      containerClassName={'border-box-no-pad'}
      paddingMode="content"
      height={height}>
      {body}
    </ContentBox>
  );
};

export default UniqueCvesTrend;
